import { agentActivityLine, type AgentPanelState } from '../stores/useAgentActivityStore';

/**
 * What one person's line says, strongest signal first.
 *
 * `working` and `waiting` can only come from the agent rail, which says what an
 * agent is doing; `typing` is everything a keepalive is entitled to claim. A
 * person on both rails takes the agent rail's word, which is what stops an agent
 * that types while it reports from occupying two lines that say the same thing.
 */
export type ActivityStatus = 'waiting' | 'working' | 'typing';

/** One person the bar has a line for. A person is a human or an agent. */
export interface ActivePerson {
  id: string;
  name: string;
  status: ActivityStatus;
  /**
   * An agent, so its turn can be stopped. Read from the roster, or from being on
   * the agent rail at all — a human never reaches that rail, and an agent whose
   * roster read failed is still an agent.
   */
  agent: boolean;
  /** Whether the panel would have anything to show about them. Never true for a person. */
  detailed: boolean;
}

/** Someone the typing rail knows about, which is a display name and nothing else. */
export interface Typist {
  id: string;
  name: string;
}

/** An agent's line on the activity rail, which is where it says what it is doing. */
export interface AgentLine {
  id: string;
  status: 'working' | 'waiting';
  /** Whether it has reported anything a block would render. */
  detailed: boolean;
}

/**
 * One line per person, deduplicated by who rather than by which rail spoke.
 *
 * The roster supplies the name wherever it can, because the agent rail carries
 * an id alone; the typing rail's own name covers an agent whose roster read
 * failed, and the id itself is the last resort, which at least keeps two agents
 * apart.
 */
export function mergeActivePeople(
  typists: Typist[],
  agentLines: AgentLine[],
  roster: ReadonlyMap<string, string>,
): ActivePerson[] {
  const people = new Map<string, ActivePerson>();

  for (const typist of typists) {
    people.set(typist.id, {
      id: typist.id,
      name: roster.get(typist.id) ?? typist.name,
      status: 'typing',
      agent: roster.has(typist.id),
      detailed: false,
    });
  }

  for (const line of agentLines) {
    people.set(line.id, {
      id: line.id,
      name: roster.get(line.id) ?? people.get(line.id)?.name ?? line.id,
      status: line.status,
      agent: true,
      detailed: line.detailed,
    });
  }

  return [...people.values()].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** One phrase of the collapsed line. */
export interface ActivitySegment {
  status: ActivityStatus;
  text: string;
}

/**
 * Above this many people a group states a number instead of naming them.
 *
 * Naming keeps the social signal in the case that is overwhelmingly the common
 * one, and a count is what still reads at a glance once a list would not.
 */
const NAMES_SHOWN = 2;

/**
 * The order the groups are read in, ending on the one that asks the reader to do
 * something.
 */
const GROUP_ORDER: ActivityStatus[] = ['typing', 'working', 'waiting'];

const PHRASING: Record<ActivityStatus, { verb: string; noun: string }> = {
  typing: { verb: 'typing', noun: 'people' },
  working: { verb: 'working', noun: 'agents' },
  waiting: { verb: 'waiting for you', noun: 'agents' },
};

/**
 * The collapsed line, as one phrase per group.
 *
 * Waiting is its own phrase and is never added into another group's number:
 * it is the only state that asks the reader to act, and a total that swallowed
 * it would hide exactly the thing worth showing.
 */
export function collapsedSegments(people: ActivePerson[]): ActivitySegment[] {
  const segments: ActivitySegment[] = [];
  for (const status of GROUP_ORDER) {
    const names = people.filter(person => person.status === status).map(person => person.name);
    if (names.length === 0) continue;
    segments.push({ status, text: phrase(status, names) });
  }
  return segments;
}

function phrase(status: ActivityStatus, names: string[]): string {
  const { verb, noun } = PHRASING[status];
  if (names.length === 1) return `${names[0]} is ${verb}`;
  if (names.length <= NAMES_SHOWN) return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} are ${verb}`;
  return `${names.length} ${noun} ${verb}`;
}

/**
 * Both rails' who-and-what as one string.
 *
 * The collapsed line subscribes to this rather than to either store's state:
 * an agent's output changes several times a second and a typist's keepalive
 * lands every two seconds, and neither may re-render a line whose words did not
 * change. The separators are control characters, which no id or display name
 * contains.
 */
const FIELD = '\u0000';
const RECORD = '\u0001';

export function typistSignature(
  typing: Record<string, { displayName: string }> | undefined,
): string {
  if (!typing) return '';
  return Object.keys(typing)
    .sort()
    .map(userId => `${userId}${FIELD}${typing[userId].displayName}`)
    .join(RECORD);
}

export function parseTypists(signature: string): Typist[] {
  if (signature === '') return [];
  return signature.split(RECORD).map(record => {
    const [id, name] = record.split(FIELD);
    return { id, name };
  });
}

/**
 * Whether a block would have anything under its header.
 *
 * It is a fact about content, but it changes at most once a turn — the first
 * report — so it belongs in the signature rather than costing a subscription to
 * the content itself.
 */
function hasReported(agent: AgentPanelState): boolean {
  return agent.plan.length > 0 || agent.activities.length > 0 || agent.answer !== '';
}

const DETAILED = '1';
const BARE = '0';

export function agentLineSignature(
  agents: Record<string, AgentPanelState> | undefined,
): string {
  if (!agents) return '';
  return Object.keys(agents)
    .sort()
    .map(agentId => [agentId, agents[agentId]] as const)
    .filter(([, agent]) => agentActivityLine(agent) !== 'idle')
    .map(([agentId, agent]) =>
      [agentId, agentActivityLine(agent), hasReported(agent) ? DETAILED : BARE].join(FIELD))
    .join(RECORD);
}

export function parseAgentLines(signature: string): AgentLine[] {
  if (signature === '') return [];
  return signature.split(RECORD).map(record => {
    const [id, status, detailed] = record.split(FIELD);
    return { id, status: status as AgentLine['status'], detailed: detailed === DETAILED };
  });
}
